import { ProgressEvent as ProgressEventEnum } from '../enums/Tokenization';
import { ErrorCode, PrimerClientError } from '../errors';
import { PaymentMethodToken } from '../types';
import { APIErrorShape } from './Api';
import { ISuccessSceneHandler } from './SuccessSceneHandler';

type ValueOf<T> = T[keyof T];

interface IProgressEvent<T extends ValueOf<ProgressEventEnum>> {
  type: T;
}

type ProgressEvent =
  | IProgressEvent<'tokenize-started'>
  | (IProgressEvent<'tokenize-success'> & { data: PaymentMethodToken })
  | (IProgressEvent<'tokenize-error'> & { data: PrimerClientError });

export type SuccessCallbackOptions = {
  successSceneHandler?: ISuccessSceneHandler;
};
export interface ProgressNotifierCallbacks {
  onTokenizeProgress(event: ProgressEvent): void;
  onTokenizeStart(): void;
  onTokenizeEnd(): void;
  onTokenizeError(error: PrimerClientError): void;
  onTokenizeSuccess(
    token: PaymentMethodToken,
    options?: SuccessCallbackOptions,
  ): void;
}

export class ProgressNotifier {
  private callbacks: ProgressNotifierCallbacks;

  constructor(callbacks: ProgressNotifierCallbacks) {
    this.callbacks = callbacks;
  }

  start(): void {
    this.callbacks.onTokenizeProgress({ type: 'tokenize-started' });
    this.callbacks.onTokenizeStart();
  }

  success(token: PaymentMethodToken, options?: SuccessCallbackOptions) {
    this.callbacks.onTokenizeProgress({
      type: 'tokenize-success',
      data: token,
    });
    this.callbacks.onTokenizeEnd();
    this.callbacks.onTokenizeSuccess(token, options);
  }

  error(error: string | APIErrorShape | PrimerClientError) {
    const err = formatError(error);
    this.callbacks.onTokenizeProgress({ type: 'tokenize-error', data: err });
    this.callbacks.onTokenizeEnd();
    this.callbacks.onTokenizeError(err);
  }
}

function formatError(
  error: string | APIErrorShape | PrimerClientError,
): PrimerClientError {
  if (error instanceof PrimerClientError) {
    return error;
  }

  return PrimerClientError.fromErrorCode(
    ErrorCode.TOKENIZATION_ERROR,
    typeof error === 'string' ? { message: error } : error,
  );
}
